// ─────────────────────────────────────────────────────────────────────────────
// DagScaleView — the DAG "Scale" view: a level-of-detail grouping treemap drawn
// on a <canvas>, driven by a ScaleModel. This is a faithful React 19 port of the
// proven vanilla-JS sandbox (vertical_fd_demo/dag_standalone/index.html — the
// canvas "scale" renderer). The drawing logic (renderCanvas, drawGroupGrid,
// drawConnectors, drawRibbon, drawLineage, drawFitText, roundRect, fitCanvas,
// clampView, centerOnSelection, zoomToTile, scaleTileAt + the pointer/wheel/
// tooltip/panel wiring) is ported verbatim in behaviour; only the DATA SOURCE is
// swapped from the sandbox's synId/synMetric/lineageCone/SYN to the uniform
// ScaleModel data contract (dagScaleModel.ts).
//
// React specifics that are correctness-critical:
//   • VIEW = {z,x,y} is a useRef (NOT state) — it mutates every wheel/drag frame
//     and must never trigger a re-render. The imperative redraw() reads it.
//   • <canvas> + ResizeObserver re-fit + redraw on container resize. jsdom has no
//     2d context and no ResizeObserver, so we guard both and never throw on mount.
//   • The wheel handler is attached via addEventListener(..., {passive:false}) in
//     an effect (React's onWheel is passive — preventDefault would be a no-op).
//   • Hi-DPI backing store is sized ONCE per resize (the sandbox's CANVAS_SIZED
//     trick); ctx.setTransform(dpr,…) is applied each frame.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ScaleModel, buildSyntheticModel, fmtCount } from "./dagScaleModel";

// ── constants (verbatim from the sandbox) ───────────────────────────────────
const BOX = { w: 168, h: 46 };
const GAP = { x: 110 };
const ROW_GATE = 5.5;
const Z_MIN = 0.08;
const Z_MAX = 160;
const bandX = (L: number) => 70 + L * (BOX.w + GAP.x);

const KIND_C: Record<string, string> = {
  geometry: "#0a0c0b",
  run: "#006d3c",
  function: "#3974b8",
  operation: "#3974b8",
  saved_plot: "#a77a20",
  plot: "#a77a20",
};
const kindColour = (k: string) => KIND_C[k] ?? "#3974b8";

const withAlpha = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

type View = { z: number; x: number; y: number };
type Tile = { lx: number; rx: number; top: number; bot: number; cy: number; L: number; i0: number; i1: number; size: number };
type Selection = { L: number; i0: number; i1: number; cone: (layer: number, a: number, b: number) => boolean };

// The segmented control: "real" uses the passed-in model; the rest build synthetic models.
type ScaleKey = "real" | "1k" | "10k" | "100k" | "1M";
const SCALE_N: Record<Exclude<ScaleKey, "real">, number> = { "1k": 1000, "10k": 10000, "100k": 100000, "1M": 1000000 };
const SCALE_KEYS: ScaleKey[] = ["real", "1k", "10k", "100k", "1M"];

// ── pure draw helpers (verbatim) ─────────────────────────────────────────────
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Always fit text to maxW: shrink the font (down to 62% of its size), then truncate with an ellipsis.
function drawFitText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, fontStr: string, color: string) {
  if (maxW < 10) return;
  ctx.font = fontStr;
  ctx.fillStyle = color;
  let w = ctx.measureText(text).width;
  if (w > maxW) {
    const m = fontStr.match(/(\d+(?:\.\d+)?)px/);
    if (m && m[1]) {
      const px = parseFloat(m[1]);
      const shrunk = Math.max(px * 0.62, (px * maxW) / w);
      ctx.font = fontStr.replace(/(\d+(?:\.\d+)?)px/, shrunk.toFixed(1) + "px");
      w = ctx.measureText(text).width;
    }
    if (w > maxW) {
      let t = text;
      while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
      text = t + "…";
    }
  }
  ctx.fillText(text, x, y);
}

// ── detail panel (docked) — JSX, NOT innerHTML, so it inherits the modal CSS ──
type PanelData = {
  kind: string;
  title: string;
  up: number;
  down: number;
  inputs: number;
  json: string;
  realId: string | null;
  single: boolean;
};

function DagNodeDetailPanel({
  data,
  onZoom,
  onActivate,
  onClose,
}: {
  data: PanelData;
  onZoom: () => void;
  onActivate?: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <aside className="v5-dag-side-panel v5-dag-scale-panel">
      <div className="m-head">
        <span className="m-kind">{data.kind}</span>
        <h3 className="m-title">{data.title}</h3>
        <button className="m-x" type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="m-body">
        <div className="m-stat">
          <div>
            <b>{data.up}</b>
            <span>upstream</span>
          </div>
          <div>
            <b>{data.down}</b>
            <span>downstream</span>
          </div>
          <div>
            <b>{data.inputs}</b>
            <span>direct inputs</span>
          </div>
        </div>
        <pre>{data.json}</pre>
        <div className="m-actions">
          <button className="m-btn primary" type="button" onClick={onZoom}>
            {data.single ? "Zoom to run" : "Zoom into group"}
          </button>
          {data.realId && onActivate ? (
            <button className="m-btn" type="button" onClick={() => onActivate(data.realId!)}>
              Open row ↗
            </button>
          ) : null}
          <button className="m-btn" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </aside>
  );
}

// ── the view ─────────────────────────────────────────────────────────────────
export function DagScaleView({
  model,
  onPickReal,
  onActivate,
}: {
  model: ScaleModel; // already built by the parent (real or synthetic)
  onPickReal?: (id: string) => void; // re-root the other views on a real single-node click
  onActivate?: (id: string) => void; // "open row ↗" deliberate jump
}): JSX.Element {
  // Which scale demo is active. "real" → the passed model; otherwise a synthetic build.
  const [scale, setScale] = useState<ScaleKey>("real");
  // Synthetic models are memoised per N so toggling does not rebuild on every redraw.
  const syntheticCache = useRef<Map<number, ScaleModel>>(new Map());
  const activeModel = useMemo<ScaleModel>(() => {
    if (scale === "real") return model;
    const N = SCALE_N[scale];
    const cache = syntheticCache.current;
    let m = cache.get(N);
    if (!m) {
      m = buildSyntheticModel(N);
      cache.set(N, m);
    }
    return m;
  }, [scale, model]);

  // VIEW mutates every frame — a ref, NEVER state (must not re-render).
  const VIEW = useRef<View>({ z: 1, x: 0, y: 0 });
  // The model fitZ baseline lives on the active model; we keep a ref so redraw reads the live value.
  const modelRef = useRef<ScaleModel>(activeModel);
  modelRef.current = activeModel;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const sizedRef = useRef(false); // CANVAS_SIZED trick: resize the backing store OUT of the per-frame path
  const tilesRef = useRef<Tile[]>([]); // current frame's hit-test rects
  const selRef = useRef<Selection | null>(null); // highlighted lineage

  // Tooltip + readout + docked panel are React state (low-frequency).
  const [tip, setTip] = useState<{ x: number; y: number; kind: string; title: string; rows: string[]; hint: string } | null>(null);
  const [readout, setReadout] = useState("");
  const [panel, setPanel] = useState<PanelData | null>(null);

  // ── geometry helpers (read VIEW.current) ──
  const worldVP = useCallback((W: number, H: number) => {
    const v = VIEW.current;
    return { x0: -v.x / v.z, y0: -v.y / v.z, x1: (W - v.x) / v.z, y1: (H - v.y) / v.z };
  }, []);
  const bandVisible = useCallback((L: number, vb: { x0: number; x1: number }) => bandX(L) + BOX.w >= vb.x0 && bandX(L) <= vb.x1, []);

  // ── canvas size (clientW/H of the wrapper) ──
  const stageSize = useCallback(() => {
    const el = wrapRef.current;
    return { W: el?.clientWidth || 800, H: el?.clientHeight || 600 };
  }, []);

  // ── fit-to-fill: the overview baseline (1 labelled box per layer) ──
  const fitCanvas = useCallback(() => {
    const m = modelRef.current;
    const { W, H } = stageSize();
    const minX = bandX(0);
    const maxX = bandX(m.nL - 1) + BOX.w;
    const worldW = maxX - minX;
    const worldH = Math.max(1, ...m.bandH);
    const pad = 50;
    const z = Math.min((W - 2 * pad) / worldW, (H - 2 * pad) / worldH);
    m.fitZ = z; // the overview baseline
    VIEW.current = { z, x: (W - worldW * z) / 2 - minX * z, y: H / 2 };
  }, [stageSize]);

  // ── clamp the view so content stays on screen (lens isn't a rectangle) ──
  const clampView = useCallback(() => {
    const m = modelRef.current;
    const { W, H } = stageSize();
    const v = VIEW.current;
    const minXw = bandX(0) + BOX.w / 2;
    const maxXw = bandX(m.nL - 1) + BOX.w / 2;
    v.x = Math.max(W / 2 - maxXw * v.z, Math.min(W / 2 - minXw * v.z, v.x));
    const cxw = (W / 2 - v.x) / v.z;
    const L = Math.max(0, Math.min(m.nL - 1, Math.round((cxw - 70) / (BOX.w + GAP.x))));
    const sh = (m.bandH[L] ?? 0) * v.z;
    v.y = sh <= H ? H / 2 : Math.max(H - sh / 2, Math.min(sh / 2, v.y));
  }, [stageSize]);

  // ── lineage predicate for the current selection ──
  const onLineage = useCallback((L: number, i0: number, i1: number) => {
    const sel = selRef.current;
    return !sel || sel.cone(L, i0, i1);
  }, []);

  // ── ribbon: ONE fill for the whole bundle (no per-edge draw) ──
  const drawRibbon = useCallback((ctx: CanvasRenderingContext2D, L: number) => {
    const m = modelRef.current;
    const x0 = bandX(L) + BOX.w;
    const x1 = bandX(L + 1);
    const hL = m.bandH[L] ?? 0;
    const hR = m.bandH[L + 1] ?? 0;
    const mx = (x0 + x1) / 2;
    const flow = m.flow[L] ?? 0;
    ctx.fillStyle = `rgba(0,109,60,${0.05 + 0.34 * (m.maxFlow ? flow / m.maxFlow : 0)})`;
    ctx.beginPath();
    ctx.moveTo(x0, -hL / 2);
    ctx.bezierCurveTo(mx, -hL / 2, mx, -hR / 2, x1, -hR / 2);
    ctx.lineTo(x1, hR / 2);
    ctx.bezierCurveTo(mx, hR / 2, mx, hL / 2, x0, hL / 2);
    ctx.closePath();
    ctx.fill();
  }, []);

  // ── per-node progressive-disclosure info (single tiles) ──
  const drawNodeInfo = useCallback(
    (ctx: CanvasRenderingContext2D, L: number, i0: number, i1: number, bx: number, cy: number, bw: number, bh: number, scaleF: number) => {
      const sh = bh * scaleF;
      if (sh < 11 || bw * scaleF < 22) return;
      const m = modelRef.current;
      const col = kindColour(m.layerKind(L));
      const info = m.tileInfo(L, i0, i1);
      const pad = 6 / scaleF;
      const F = (px: number) => px / scaleF + "px";
      const maxW = bw - 2 * pad;
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      if (sh < 26) {
        drawFitText(ctx, info.title, bx + pad, cy, maxW, F(Math.min(sh * 0.6, 11)) + " ui-monospace,monospace", col);
        return;
      }
      drawFitText(ctx, m.layerKind(L), bx + pad, cy - bh * 0.26, maxW, F(9) + " ui-monospace,monospace", col);
      drawFitText(ctx, info.title, bx + pad, cy + (sh >= 38 ? -bh * 0.02 : 0), maxW, "600 " + F(Math.min(sh * 0.3, 14)) + " -apple-system,sans-serif", "#0a0c0b");
      if (sh >= 38 && info.sub) drawFitText(ctx, info.sub, bx + pad, cy + bh * 0.28, maxW, F(10) + " ui-monospace,monospace", "#6f766d");
    },
    [],
  );

  // ── group-tile info (multi-member tiles) ──
  const drawGroupInfo = useCallback((ctx: CanvasRenderingContext2D, L: number, i0: number, i1: number, bx: number, cy: number, bw: number, bh: number) => {
    if (bh < 13 || bw < 40) return;
    const m = modelRef.current;
    const size = i1 - i0;
    const whole = size >= (m.count[L] ?? size);
    const info = m.tileInfo(L, i0, i1);
    const maxW = bw - 16;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    if (bh < 30 || whole || !info.sub) {
      drawFitText(ctx, info.title, bx + 8, cy, maxW, "600 13px -apple-system,sans-serif", "#0a0c0b");
      return;
    }
    drawFitText(ctx, info.title, bx + 8, cy - bh * 0.16, maxW, "600 14px -apple-system,sans-serif", "#0a0c0b");
    drawFitText(ctx, info.sub, bx + 8, cy + bh * 0.2, maxW, "10px ui-monospace,monospace", "#6f766d");
  }, []);

  // ── RECURSIVE TREEMAP for one layer (overview → groups → single runs) ──
  const drawGroupGrid = useCallback(
    (ctx: CanvasRenderingContext2D, L: number, W: number, H: number, tilesOut: Tile[]) => {
      const m = modelRef.current;
      const c = m.count[L] ?? 0;
      const col = kindColour(m.layerKind(L));
      const v = VIEW.current;
      const z = v.z;
      const h = m.bandH[L] ?? 0;
      const sh = h * z;
      const gap = Math.min(44, (BOX.w + GAP.x) * z * 0.22);
      const slotW = (BOX.w + GAP.x) * z - gap;
      const slotX = v.x + (bandX(L) + BOX.w / 2) * z - slotW / 2;
      if (slotX + slotW < 0 || slotX > W) return 0;
      const pad = 5;
      const innerX = slotX + pad;
      const innerW = slotW - 2 * pad;
      if (innerW < 30) return 0;
      // 1 box at the overview fit, ×4 every 1.5× zoom, capped at count (single runs)
      const level = Math.max(0, Math.log(Math.max(z / (m.fitZ || z), 1)) / Math.log(1.5));
      const tiles = Math.max(1, Math.min(c, Math.round(Math.pow(4, level))));
      const C = Math.max(1, Math.min(Math.min(8, Math.round(innerW / 180)), tiles));
      const tileW = innerW / C;
      const g = Math.ceil(c / tiles);
      const rows = Math.ceil(tiles / C);
      const overview = sh <= H + 2; // layer fits the viewport → span its band extent
      const tileH = overview ? sh / rows : Math.max(46, Math.min(132, H / 7));
      const top = v.y - sh / 2;
      const vcY = (H / 2 - v.y) / z;
      const frac = Math.max(0, Math.min(1, (vcY + h / 2) / h));
      const rf = overview ? 0 : frac * Math.max(0, rows - 1);
      const screenY = (r: number) => (overview ? top + (r + 0.5) * tileH : H / 2 + (r - rf) * tileH);
      let r0: number, r1: number;
      if (overview) {
        r0 = 0;
        r1 = rows - 1;
      } else {
        r0 = Math.max(0, Math.floor(rf - H / (2 * tileH)) - 1);
        r1 = Math.min(rows - 1, Math.ceil(rf + H / (2 * tileH)) + 1);
      }
      let n = 0;
      const sel = selRef.current;
      for (let r = r0; r <= r1 && n < 260; r++) {
        const cy = screenY(r);
        if (cy < -tileH || cy > H + tileH) continue;
        for (let cc = 0; cc < C; cc++) {
          const t = r * C + cc;
          if (t >= tiles) break;
          const i0 = t * g;
          const i1 = Math.min((t + 1) * g, c);
          const size = i1 - i0;
          if (size <= 0) continue;
          const tx = innerX + cc * tileW;
          const bw = tileW - 5;
          const bh = Math.max(2, tileH - 5);
          const lin = onLineage(L, i0, i1);
          const isSel = sel && L === sel.L && i0 === sel.i0 && i1 === sel.i1;
          ctx.globalAlpha = lin ? 1 : 0.16; // grey out everything off the selected link
          ctx.fillStyle = size === 1 ? "#fff" : withAlpha(lin ? col : "#9aa197", 0.12);
          ctx.strokeStyle = lin ? col : "#9aa197";
          ctx.lineWidth = isSel ? 2.6 : size === 1 ? 1.4 : 1.5;
          roundRect(ctx, tx, cy - bh / 2, bw, bh, Math.min(7, bh / 3));
          ctx.fill();
          ctx.stroke();
          if (lin && bh >= 15 && bw >= 40) {
            if (size === 1) drawNodeInfo(ctx, L, i0, i1, tx, cy, bw, bh, 1);
            else drawGroupInfo(ctx, L, i0, i1, tx, cy, bw, bh);
          }
          ctx.globalAlpha = 1;
          tilesOut.push({ lx: tx, rx: tx + bw, top: cy - bh / 2, bot: cy + bh / 2, cy, L, i0, i1, size });
          n++;
        }
      }
      if (innerW > 50) {
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        const header = m.tileInfo(L, 0, c).single ? `${fmtCount(c)}` : m.tileInfo(L, 0, c).title;
        drawFitText(ctx, header, Math.max(6, innerX), 14, innerW - 4, "600 11px -apple-system,sans-serif", col);
      }
      return n + 1;
    },
    [drawGroupInfo, drawNodeInfo, onLineage],
  );

  // ── aggregate fan-in/out curves between group tiles ──
  const drawConnectors = useCallback(
    (ctx: CanvasRenderingContext2D, tilesByL: Record<number, Tile[]>) => {
      const m = modelRef.current;
      const sel = selRef.current;
      for (let L = 0; L < m.nL - 1; L++) {
        const src = tilesByL[L];
        const dst = tilesByL[L + 1];
        if (!src || !dst || !src.length || !dst.length) continue;
        const baseCol = kindColour(m.layerKind(L));
        let n = 0;
        for (const T of dst) {
          const tlin = onLineage(L + 1, T.i0, T.i1);
          for (const S of src) {
            if (!m.connects(L, S.i0, S.i1, T.i0, T.i1)) continue; // analytic/real wiring from the model
            const lin = !sel || (tlin && onLineage(L, S.i0, S.i1));
            ctx.strokeStyle = lin ? withAlpha(sel ? "#006d3c" : baseCol, sel ? 0.85 : 0.42) : withAlpha("#9aa197", 0.1);
            ctx.lineWidth = lin && sel ? 2 : 1.3;
            const ax = S.rx;
            const ay = S.cy;
            const bx = T.lx;
            const by = T.cy;
            const mx = (ax + bx) / 2;
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.bezierCurveTo(mx, ay, mx, by, bx, by);
            ctx.stroke();
            if (++n > 500) break;
          }
          if (n > 500) break;
        }
      }
    },
    [onLineage],
  );

  // ── the master per-frame draw ──
  const redraw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return; // jsdom has no 2d context — bail (never throw on mount)
    const m = modelRef.current;
    const { W, H } = stageSize();
    const dpr = Math.min(2, (typeof window !== "undefined" && window.devicePixelRatio) || 1);
    if (!sizedRef.current) {
      cv.width = W * dpr;
      cv.height = H * dpr;
      sizedRef.current = true; // resize OUT of the per-frame path
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const t0 = typeof performance !== "undefined" ? performance.now() : 0;
    const v = VIEW.current;
    const vb = worldVP(W, H);

    // ribbons flow behind, in world space
    ctx.save();
    ctx.translate(v.x, v.y);
    ctx.scale(v.z, v.z);
    let draws = 0;
    for (let L = 0; L < m.nL - 1; L++) {
      if (bandX(L + 1) + BOX.w < vb.x0 || bandX(L) > vb.x1) continue; // gap-span cull
      drawRibbon(ctx, L);
      draws++;
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // recursive treemap per visible band (screen space)
    const tilesByL: Record<number, Tile[]> = {};
    const allTiles: Tile[] = [];
    for (let L = 0; L < m.nL; L++) {
      if (!bandVisible(L, vb) || (m.bandH[L] ?? 0) * v.z < ROW_GATE) continue;
      const arr: Tile[] = [];
      draws += drawGroupGrid(ctx, L, W, H, arr);
      tilesByL[L] = arr;
      allTiles.push(...arr);
    }
    tilesRef.current = allTiles;
    drawConnectors(ctx, tilesByL); // aggregate fan-in/out on top

    const level = Math.max(0, Math.log(Math.max(v.z / (m.fitZ || v.z), 1)) / Math.log(1.5));
    const ms = typeof performance !== "undefined" ? (performance.now() - t0).toFixed(1) : "0.0";
    const lvl = level < 0.6 ? "layers" : level > 7 ? "individual runs" : "groups";
    setReadout(`${m.n.toLocaleString()} nodes · ${lvl} · z=${v.z.toFixed(2)} · ${draws} draws · ${ms} ms/frame`);
  }, [bandVisible, drawConnectors, drawGroupGrid, drawRibbon, stageSize, worldVP]);

  // ── hit-testing ──
  const scaleTileAt = useCallback((mx: number, my: number): Tile | null => {
    const tiles = tilesRef.current;
    for (let i = tiles.length - 1; i >= 0; i--) {
      const t = tiles[i]!;
      if (mx >= t.lx && mx <= t.rx && my >= t.top && my <= t.bot) return t;
    }
    return null;
  }, []);

  // ── zoom helpers ──
  const zoomToTile = useCallback(
    (t: Tile) => {
      const { W, H } = stageSize();
      const v = VIEW.current;
      const cx = (t.lx + t.rx) / 2;
      const cy = (t.top + t.bot) / 2;
      const wx = (cx - v.x) / v.z;
      const wy = (cy - v.y) / v.z;
      v.z = Math.min(Z_MAX, v.z * 2.6);
      v.x = W / 2 - wx * v.z;
      v.y = H / 2 - wy * v.z;
      clampView();
      redraw();
    },
    [clampView, redraw, stageSize],
  );

  // Position the view so the SELECTED group sits at the viewport centre at the current zoom.
  const centerOnSelection = useCallback(() => {
    const sel = selRef.current;
    if (!sel) return;
    const m = modelRef.current;
    const { W, H } = stageSize();
    const v = VIEW.current;
    const L = sel.L;
    const z = v.z;
    const h = m.bandH[L] ?? 0;
    const sh = h * z;
    const c = m.count[L] ?? 1;
    v.x = W / 2 - (bandX(L) + BOX.w / 2) * z;
    if (sh <= H + 2) {
      v.y = H / 2;
      return;
    }
    const gap = Math.min(44, (BOX.w + GAP.x) * z * 0.22);
    const innerW = (BOX.w + GAP.x) * z - gap - 10;
    const level = Math.max(0, Math.log(Math.max(z / (m.fitZ || z), 1)) / Math.log(1.5));
    const tiles = Math.max(1, Math.min(c, Math.round(Math.pow(4, level))));
    const C = Math.max(1, Math.min(Math.min(8, Math.round(innerW / 180)), tiles));
    const rows = Math.ceil(tiles / C);
    const g = Math.ceil(c / tiles);
    const rowSel = Math.floor(Math.floor((sel.i0 + sel.i1) / 2 / g) / C);
    const fracVp = rows > 1 ? rowSel / (rows - 1) : 0.5;
    const vcY = fracVp * h - h / 2;
    v.y = H / 2 - vcY * z;
  }, [stageSize]);

  // ── selection: build the lineage predicate, open the docked panel ──
  const selectTile = useCallback(
    (t: Tile) => {
      const m = modelRef.current;
      // Single REAL node → re-root the other views instead of (or as well as) selecting.
      if (t.size === 1) {
        const realId = m.realIdAt(t.L, t.i0);
        if (realId && onPickReal) onPickReal(realId);
      }
      selRef.current = { L: t.L, i0: t.i0, i1: t.i1, cone: m.coneOf(t.L, t.i0, t.i1) };
      setTip(null);
      const d = m.detail(t.L, t.i0, t.i1);
      setPanel({ kind: d.kind, title: d.title, up: d.up, down: d.down, inputs: d.inputs, json: d.json, realId: d.realId, single: t.size === 1 });
      redraw();
    },
    [onPickReal, redraw],
  );

  const clearSelection = useCallback(() => {
    selRef.current = null;
    setPanel(null);
    redraw();
  }, [redraw]);

  // The "Zoom into group / Zoom to run" panel action re-derives the selected tile rect from VIEW.
  const zoomToSelection = useCallback(() => {
    const sel = selRef.current;
    if (!sel) return;
    const v = VIEW.current;
    const cx = (bandX(sel.L) + BOX.w / 2) * v.z + v.x;
    // approximate the tile centre y from the current frame's tiles, fall back to the spine
    let cy = stageSize().H / 2;
    for (const t of tilesRef.current) {
      if (t.L === sel.L && t.i0 === sel.i0 && t.i1 === sel.i1) {
        cy = t.cy;
        break;
      }
    }
    zoomToTile({ lx: cx - 1, rx: cx + 1, top: cy - 1, bot: cy + 1, cy, L: sel.L, i0: sel.i0, i1: sel.i1, size: sel.i1 - sel.i0 });
  }, [stageSize, zoomToTile]);

  // ── tooltip wiring ──
  const showTipFor = useCallback((t: Tile, clientX: number, clientY: number) => {
    const m = modelRef.current;
    const info = m.tileInfo(t.L, t.i0, t.i1);
    const rows = [info.sub ?? "", `layer ${t.L + 1}/${m.nL}`].filter(Boolean);
    setTip({
      x: Math.min(clientX + 14, (typeof window !== "undefined" ? window.innerWidth : 1200) - 300),
      y: Math.min(clientY + 14, (typeof window !== "undefined" ? window.innerHeight : 800) - 120),
      kind: m.layerKind(t.L),
      title: info.title,
      rows,
      hint: info.single ? "click for detail" : "click to drill in",
    });
  }, []);

  // ── pointer + wheel wiring ──
  // The wheel handler must be a non-passive native listener (React's onWheel is passive).
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setTip(null);
      const f = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const v = VIEW.current;
      if (selRef.current) {
        v.z = Math.max(Z_MIN, Math.min(Z_MAX, v.z * f));
        centerOnSelection();
        clampView();
        redraw();
        return;
      }
      const r = cv.getBoundingClientRect();
      const cx = e.clientX - r.left;
      const cy = e.clientY - r.top;
      const wx = (cx - v.x) / v.z;
      const wy = (cy - v.y) / v.z;
      v.z = Math.max(Z_MIN, Math.min(Z_MAX, v.z * f));
      v.x = cx - wx * v.z;
      v.y = cy - wy * v.z;
      clampView();
      redraw();
    };
    cv.addEventListener("wheel", onWheel, { passive: false });
    return () => cv.removeEventListener("wheel", onWheel);
  }, [centerOnSelection, clampView, redraw]);

  // Pointer drag (pan) + click (select / clear) as native window listeners so a drag that
  // leaves the canvas still tracks. The mousedown is a React handler on the canvas.
  const dragRef = useRef<{ x: number; y: number; vx: number; vy: number; moved: boolean } | null>(null);
  // Guard the window-level listeners so a mousemove/up in flight after unmount no-ops (no stale setState).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const onMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const v = VIEW.current;
    dragRef.current = { x: e.clientX, y: e.clientY, vx: v.x, vy: v.y, moved: false };
  }, []);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const stageXY = (e: MouseEvent) => {
      const r = cv.getBoundingClientRect();
      return { mx: e.clientX - r.left, my: e.clientY - r.top };
    };
    const onMove = (e: MouseEvent) => {
      if (!mountedRef.current) return;
      const drag = dragRef.current;
      if (drag) {
        if (Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) > 3) {
          drag.moved = true;
          setTip(null);
        }
        const v = VIEW.current;
        v.x = drag.vx + (e.clientX - drag.x);
        v.y = drag.vy + (e.clientY - drag.y);
        clampView();
        redraw();
        return;
      }
      const { mx, my } = stageXY(e);
      const t = scaleTileAt(mx, my);
      if (t) {
        showTipFor(t, e.clientX, e.clientY);
        cv.style.cursor = "pointer";
      } else {
        setTip(null);
        cv.style.cursor = "default";
      }
    };
    const onUp = (e: MouseEvent) => {
      if (!mountedRef.current) return;
      const drag = dragRef.current;
      if (drag && !drag.moved) {
        const { mx, my } = stageXY(e);
        const t = scaleTileAt(mx, my);
        if (t) selectTile(t);
        else if (selRef.current) clearSelection();
      }
      dragRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [clampView, clearSelection, redraw, scaleTileAt, selectTile, showTipFor]);

  // ── fit + redraw when the active model changes (toggle real ↔ synthetic) ──
  useEffect(() => {
    sizedRef.current = false;
    selRef.current = null;
    setPanel(null);
    setTip(null);
    fitCanvas();
    redraw();
  }, [activeModel, fitCanvas, redraw]);

  // ── ResizeObserver: re-fit + redraw on container resize (guarded for jsdom) ──
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return; // jsdom has no ResizeObserver
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      sizedRef.current = false;
      fitCanvas();
      redraw();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitCanvas, redraw]);

  return (
    <div className="v5-dag-scale" data-testid="v5-dag-scale">
      <div className="v5-dag-scale-controls">
        {SCALE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={"v5-dag-scale-seg" + (scale === key ? " is-on" : "")}
            onClick={() => setScale(key)}
          >
            {key}
          </button>
        ))}
      </div>
      <div className="v5-dag-scale-stage" ref={wrapRef}>
        <canvas ref={canvasRef} className="v5-dag-scale-canvas" onMouseDown={onMouseDown} />
        {readout ? <div className="v5-dag-scale-readout">{readout}</div> : null}
        {tip ? (
          <div className="v5-dag-scale-tip" style={{ left: tip.x, top: tip.y }}>
            <div className="t-kind">{tip.kind}</div>
            <div className="t-label">{tip.title}</div>
            {tip.rows.map((r, i) => (
              <div className="t-row" key={i}>
                {r}
              </div>
            ))}
            <div className="t-row t-hint">{tip.hint}</div>
          </div>
        ) : null}
      </div>
      {panel ? (
        <DagNodeDetailPanel data={panel} onZoom={zoomToSelection} onActivate={onActivate} onClose={clearSelection} />
      ) : null}
    </div>
  );
}
